<template>
    <div class="sp-paso">
        <header class="sp-paso__head">
            <span class="sp-icon-badge"><i class="pi pi-user" aria-hidden="true"></i></span>
            <div>
                <h2 class="sp-h3">Crea tu cuenta</h2>
                <p>Estos datos nos permitirán identificarte y mantener segura tu cuenta.</p>
            </div>
        </header>

        <!-- Google solo aparece si hay credencial configurada. Sin ella no se
             descarga ningún script externo. -->
        <template v-if="mostrarGoogle">
            <div class="sp-google">
                <div ref="botonGoogle" class="sp-google__boton"></div>
                <p v-if="errorGoogle" class="sp-campo__error">
                    <i class="pi pi-exclamation-circle" aria-hidden="true"></i>
                    <span>{{ errorGoogle }}</span>
                </p>
            </div>
            <div class="sp-separador"><span>o</span></div>
        </template>

        <div v-if="datos.google_token" class="sp-google-ok">
            <i class="pi pi-check-circle" aria-hidden="true"></i>
            <span>Continuando con Google como <strong>{{ datos.email }}</strong>. No necesitas contraseña.</span>
        </div>

        <div class="sp-campo">
            <label :for="id('nombre')">
                <span>Nombre completo</span><span class="sp-campo__req" aria-hidden="true">*</span>
            </label>
            <InputText
                :id="id('nombre')"
                v-model.trim="datos.nombre"
                autocomplete="name"
                :class="{ 'p-invalid': error('nombre') }"
                :aria-invalid="error('nombre') ? 'true' : 'false'"
                :aria-describedby="error('nombre') ? id('nombre') + '-err' : null"
            />
            <p v-if="error('nombre')" :id="id('nombre') + '-err'" class="sp-campo__error">
                <i class="pi pi-exclamation-circle" aria-hidden="true"></i><span>{{ error('nombre') }}</span>
            </p>
        </div>

        <div class="sp-form__row">
            <div class="sp-campo">
                <label :for="id('email')">
                    <span>Correo electrónico</span><span class="sp-campo__req" aria-hidden="true">*</span>
                </label>
                <InputText
                    :id="id('email')"
                    v-model.trim="datos.email"
                    type="email"
                    autocomplete="email"
                    :disabled="!!datos.google_token"
                    :class="{ 'p-invalid': error('email') }"
                    :aria-invalid="error('email') ? 'true' : 'false'"
                    @blur="verificarEmail"
                />
                <p v-if="error('email')" class="sp-campo__error">
                    <i class="pi pi-exclamation-circle" aria-hidden="true"></i><span>{{ error('email') }}</span>
                </p>
                <p v-else-if="emailOcupado" class="sp-campo__error">
                    <i class="pi pi-exclamation-circle" aria-hidden="true"></i>
                    <span>
                        Este correo ya está asociado a una cuenta.
                        <a href="#" @click.prevent="$emit('ir-login')">Inicia sesión</a>
                    </span>
                </p>
            </div>

            <div class="sp-campo">
                <label :for="id('celular')">
                    <span>Teléfono / celular</span><span class="sp-campo__req" aria-hidden="true">*</span>
                </label>
                <InputMask
                    :id="id('celular')"
                    v-model="datos.celular"
                    mask="999 999 9999"
                    placeholder="300 123 4567"
                    :class="{ 'p-invalid': error('celular') }"
                />
                <p v-if="error('celular')" class="sp-campo__error">
                    <i class="pi pi-exclamation-circle" aria-hidden="true"></i><span>{{ error('celular') }}</span>
                </p>
            </div>
        </div>

        <div class="sp-form__row">
            <div class="sp-campo">
                <label :for="id('tipodoc')">
                    <span>Tipo de documento</span><span class="sp-campo__req" aria-hidden="true">*</span>
                </label>
                <Dropdown
                    :id="id('tipodoc')"
                    v-model="datos.id_tipo_identificacion"
                    :options="tiposIdentificacion"
                    optionLabel="descripcion"
                    optionValue="id"
                    placeholder="Selecciona el tipo"
                    :loading="cargandoCatalogos"
                    :class="{ 'p-invalid': error('id_tipo_identificacion') }"
                />
                <p v-if="error('id_tipo_identificacion')" class="sp-campo__error">
                    <i class="pi pi-exclamation-circle" aria-hidden="true"></i><span>{{ error('id_tipo_identificacion') }}</span>
                </p>
            </div>

            <div class="sp-campo">
                <label :for="id('documento')">
                    <span>Número de documento</span><span class="sp-campo__req" aria-hidden="true">*</span>
                </label>
                <InputText
                    :id="id('documento')"
                    v-model.trim="datos.documento"
                    inputmode="numeric"
                    :class="{ 'p-invalid': error('documento') }"
                />
                <p v-if="error('documento')" class="sp-campo__error">
                    <i class="pi pi-exclamation-circle" aria-hidden="true"></i><span>{{ error('documento') }}</span>
                </p>
            </div>
        </div>

        <!-- Con Google no se pide contraseña, tal como indica el documento. -->
        <div v-if="!datos.google_token" class="sp-form__row">
            <div class="sp-campo">
                <label :for="id('password')">
                    <span>Contraseña</span><span class="sp-campo__req" aria-hidden="true">*</span>
                </label>
                <Password
                    :id="id('password')"
                    v-model="datos.password"
                    :feedback="false"
                    toggleMask
                    inputClass="w-full"
                    :class="{ 'p-invalid': error('password') }"
                />
                <p v-if="error('password')" class="sp-campo__error">
                    <i class="pi pi-exclamation-circle" aria-hidden="true"></i><span>{{ error('password') }}</span>
                </p>
                <p v-else class="sp-campo__ayuda">Mínimo 8 caracteres.</p>
            </div>

            <div class="sp-campo">
                <label :for="id('password2')">
                    <span>Confirmar contraseña</span><span class="sp-campo__req" aria-hidden="true">*</span>
                </label>
                <Password
                    :id="id('password2')"
                    v-model="datos.password_confirmation"
                    :feedback="false"
                    toggleMask
                    inputClass="w-full"
                    :class="{ 'p-invalid': error('password_confirmation') }"
                />
                <p v-if="error('password_confirmation')" class="sp-campo__error">
                    <i class="pi pi-exclamation-circle" aria-hidden="true"></i><span>{{ error('password_confirmation') }}</span>
                </p>
            </div>
        </div>
    </div>
</template>

<script>
import api from '../api';
import { montarBotonGoogle, googleDisponible } from '../google';
import { registrarEvento, EVENTOS } from '../analitica';

export default {
    name: 'PasoCuenta',
    emits: ['google', 'ir-login'],
    props: {
        datos: { type: Object, required: true },
        errores: { type: Object, default: () => ({}) },
        tiposIdentificacion: { type: Array, default: () => [] },
        cargandoCatalogos: { type: Boolean, default: false }
    },
    data() {
        return {
            emailOcupado: false,
            errorGoogle: ''
        };
    },
    computed: {
        mostrarGoogle() {
            return googleDisponible() && !this.datos.google_token;
        }
    },
    mounted() {
        if (this.mostrarGoogle) {
            montarBotonGoogle(
                this.$refs.botonGoogle,
                (idToken) => {
                    registrarEvento(EVENTOS.CLIC_GOOGLE);
                    this.$emit('google', idToken);
                },
                () => {
                    this.errorGoogle = 'No pudimos cargar el acceso con Google. Puedes continuar con tu correo.';
                }
            );
        }
    },
    methods: {
        id(campo) {
            return 'sp-cuenta-' + campo;
        },
        error(campo) {
            return this.errores[campo] || '';
        },
        /**
         * Aviso temprano de correo duplicado, sin esperar al final del wizard.
         */
        verificarEmail() {
            this.emailOcupado = false;

            const email = (this.datos.email || '').trim();
            if (!email || this.datos.google_token) return;
            if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) return;

            api.post('negocios/email-disponible', { email })
                .then((r) => {
                    const datos = r && r.data ? r.data.data : null;
                    this.emailOcupado = datos ? datos.disponible === false : false;
                })
                .catch(() => {
                    // Si el chequeo falla no se bloquea nada: el backend vuelve
                    // a validarlo al crear la cuenta.
                });
        }
    }
};
</script>

<style lang="scss" scoped>
.sp-paso {
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
}

.sp-paso__head {
    display: flex;
    align-items: center;
    gap: 1rem;

    p {
        margin-top: 0.25rem;
        color: var(--sp-text-soft);
        font-size: 0.92rem;
    }
}

.sp-google {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;
}

.sp-google__boton {
    display: flex;
    justify-content: center;
    width: 100%;
    min-height: 44px;
}

.sp-google-ok {
    display: flex;
    align-items: flex-start;
    gap: 0.6rem;
    padding: 0.85rem 1rem;
    background: var(--sp-orange-50);
    border-left: 4px solid var(--sp-orange);
    border-radius: 0 10px 10px 0;
    color: var(--sp-navy);
    font-size: 0.88rem;

    i {
        color: var(--sp-orange);
    }
}

.sp-separador {
    display: flex;
    align-items: center;
    gap: 1rem;
    color: var(--sp-text-soft);
    font-size: 0.85rem;

    &::before,
    &::after {
        content: '';
        flex: 1 1 auto;
        height: 1px;
        background: var(--sp-border);
    }
}

.sp-campo__ayuda {
    color: var(--sp-text-soft);
    font-size: 0.78rem;
}

.sp-campo__error a {
    color: var(--sp-orange);
    text-decoration: underline;
}
</style>
